import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  addDays,
  differenceInDays,
  subDays,
} from 'date-fns'

const DAY_INITIALS: Record<number, string> = {
  0: 'S', 1: 'M', 2: 'T', 3: 'W', 4: 'Th', 5: 'F', 6: 'Sa',
}

/** Format like Calendar group cards: "W, 2/18 - F, 2/20" (day initial, M/d - day initial, M/d) */
export function formatGroupDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string {
  const s = (startDate ?? '').trim()
  const e = (endDate ?? '').trim()
  if (!s && !e) return 'Dates TBD'
  if (!s && e) {
    const end = parseISO(e)
    const endDay = end.getDay()
    return `Through ${DAY_INITIALS[endDay] ?? ''}, ${format(end, 'M/d')}`
  }
  if (s && !e) {
    const start = parseISO(s)
    const startDay = start.getDay()
    return `From ${DAY_INITIALS[startDay] ?? ''}, ${format(start, 'M/d')}`
  }
  const start = parseISO(s)
  const end = parseISO(e)
  const startDay = start.getDay()
  const endDay = end.getDay()
  return `${DAY_INITIALS[startDay] ?? ''}, ${format(start, 'M/d')} - ${DAY_INITIALS[endDay] ?? ''}, ${format(end, 'M/d')}`
}

export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string {
  const s = (startDate ?? '').trim()
  const e = (endDate ?? '').trim()
  if (!s || !e) return 'Dates TBD'
  const start = parseISO(s)
  const end = parseISO(e)
  const startMonth = format(start, 'MMM')
  const endMonth = format(end, 'MMM')
  if (startMonth === endMonth) {
    return `${format(start, 'MMM d')}–${format(end, 'd')}`
  }
  return `${format(start, 'MMM d')}–${format(end, 'MMM d')}`
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return format(d, 'MMM d, yyyy')
}

export function formatShortDate(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date
  return format(d, 'MMM d')
}

export function getSundayOfWeek(date: Date): Date {
  return startOfWeek(date, { weekStartsOn: 0 })
}

export function getSaturdayOfWeek(date: Date): Date {
  return endOfWeek(date, { weekStartsOn: 0 })
}

export function doesOverlap(
  rangeStart: string | null | undefined,
  rangeEnd: string | null | undefined,
  weekStart: string,
  weekEnd: string
): boolean {
  const rs = (rangeStart ?? '').trim()
  const re = (rangeEnd ?? '').trim()
  if (!rs || !re) return false
  const rangeStartDate = parseISO(rs)
  const rangeEndDate = parseISO(re)
  const weekStartDate = parseISO(weekStart)
  const weekEndDate = parseISO(weekEnd)
  return rangeStartDate <= weekEndDate && rangeEndDate >= weekStartDate
}

/** Mon–Thu: days the team is treated as “on base” for calendar week assignment. */
function isOnBaseWeekday(d: Date): boolean {
  const day = d.getDay()
  return day >= 1 && day <= 4
}

/**
 * A group appears in a calendar week only when the trip overlaps at least one on-base day
 * (Mon–Thu) in that week’s row. No inferred placement: trips that never hit Mon–Thu never
 * appear on the weekly calendar (fix dates instead of guessing).
 */
export function isGroupInWorkWeek(
  groupStartDate: string | null | undefined,
  groupEndDate: string | null | undefined,
  weekStart: string,
  weekEnd: string
): boolean {
  const rawStart = (groupStartDate ?? '').trim()
  const rawEnd = (groupEndDate ?? '').trim()
  if (!rawStart && !rawEnd) return false

  const tripStart = parseISO((rawStart || rawEnd).split('T')[0])
  const tripEnd = parseISO((rawEnd || rawStart).split('T')[0])
  const weekStartDate = parseISO(weekStart)
  const weekEndDate = parseISO(weekEnd)
  if (
    isNaN(tripStart.getTime()) ||
    isNaN(tripEnd.getTime()) ||
    isNaN(weekStartDate.getTime()) ||
    isNaN(weekEndDate.getTime())
  ) {
    return false
  }

  let cursor = weekStartDate
  while (cursor <= weekEndDate) {
    if (isOnBaseWeekday(cursor) && cursor >= tripStart && cursor <= tripEnd) {
      return true
    }
    cursor = addDays(cursor, 1)
  }

  return false
}

export function getWeekLabel(weekStart: string, weekEnd: string): string {
  return formatDateRange(weekStart, weekEnd)
}

export function getMonthYear(date: string): string {
  return format(parseISO(date), 'MMMM yyyy')
}

export function getTripDays(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): number {
  const s = (startDate ?? '').trim()
  const e = (endDate ?? '').trim()
  if (!s || !e) return 0
  return differenceInDays(parseISO(e), parseISO(s)) + 1
}

/**
 * Get week start dates in a range. weekStartsOn: 0=Sun, 1=Mon, ..., 6=Sat. Default 6 (Saturday).
 */
export function getWeeksInRange(
  startDate: Date,
  months: number,
  weekStartsOn: number = 6
): Date[] {
  const weeks: Date[] = []
  let currentWeekStart = startOfWeek(startDate, { weekStartsOn: weekStartsOn as 0 | 1 | 2 | 3 | 4 | 5 | 6 })
  const endDate = addDays(startDate, months * 30)
  while (currentWeekStart < endDate) {
    weeks.push(currentWeekStart)
    currentWeekStart = addDays(currentWeekStart, 7)
  }
  return weeks
}

/**
 * Get the previous week's date range (the week that most recently ended).
 * For weekStartsOn=6 (Saturday): week ends Friday; returns { start, end } for that week.
 */
export function getPreviousWeekRange(
  weekStartsOn: number = 6
): { start: string; end: string } {
  const weekEndDay = (weekStartsOn + 6) % 7
  const today = new Date()
  const todayDay = today.getDay()
  let daysBack = todayDay - weekEndDay
  if (daysBack <= 0) daysBack += 7
  const lastWeekEnd = subDays(today, daysBack)
  const weekEnd = format(lastWeekEnd, 'yyyy-MM-dd')
  const weekStart = format(subDays(lastWeekEnd, 6), 'yyyy-MM-dd')
  return { start: weekStart, end: weekEnd }
}
